import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Plus, Edit, Trash2, MessageSquare, Calendar, Search, Filter } from "lucide-react";
import { AddDelegationModal } from "../components/delegation/AddDelegationModal";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";

export default function DevelopmentPersonPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { users } = useData();
    const { token } = useAuth();

    const [delegations, setDelegations] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDelegation, setSelectedDelegation] = useState<any>(null);

    // Filter states
    const [searchTerm, setSearchTerm] = useState("");
    const [levelFilter, setLevelFilter] = useState("all");

    // Delete confirm state
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    // View Notes state
    const [notesContent, setNotesContent] = useState("");
    const [isViewNotesOpen, setIsViewNotesOpen] = useState(false);

    const user = users.find(u => u.id == id);

    const fetchDelegations = async () => {
        if (!id) return;
        try {
            setLoading(true);
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/delegation/user/${id}`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (data.success) {
                setDelegations(data.delegations);
            }
        } catch (error) {
            console.error("Error fetching delegations:", error);
            toast.error("Failed to load delegations");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDelegations();
    }, [id, token]);

    const handleEdit = (delegation: any) => {
        setSelectedDelegation(delegation);
        setIsModalOpen(true);
    };

    const handleAdd = () => {
        setSelectedDelegation(null);
        setIsModalOpen(true);
    };

    const handleDeleteClick = (delegationId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setDeleteId(delegationId);
        setIsDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!deleteId) return;
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/delegation/${deleteId}`, {
                method: 'DELETE',
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (response.ok) {
                toast.success("Delegation task deleted");
                fetchDelegations();
            } else {
                toast.error("Failed to delete task");
            }
        } catch (error) {
            console.error("Error deleting task:", error);
            toast.error("Error deleting task");
        } finally {
            setIsDeleteDialogOpen(false);
            setDeleteId(null);
        }
    };

    const handleLevelChange = async (delegationId: string, newLevel: string) => {
        try {
            // Optimistic update
            const updatedDelegations = delegations.map(d =>
                d.id === delegationId ? { ...d, competenceLevel: newLevel } : d
            );
            setDelegations(updatedDelegations);

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/delegation/${delegationId}`, {
                method: 'PATCH',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ competenceLevel: newLevel })
            });

            const data = await response.json();
            if (data.success) {
                toast.success(`Competence level updated to ${newLevel}`);
            } else {
                // Revert if failed
                fetchDelegations();
                toast.error("Failed to update status");
            }
        } catch (error) {
            fetchDelegations();
            console.error("Error updating level:", error);
            toast.error("Error updating level");
        }
    };

    const handleViewNotes = (notes: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setNotesContent(notes);
        setIsViewNotesOpen(true);
    };

    // Filter logic
    const filteredDelegations = delegations.filter(item => {
        const matchesSearch = item.task.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesLevel = levelFilter === "all" || item.competenceLevel === levelFilter;

        return matchesSearch && matchesLevel;
    });

    // derived stats
    const stats = {
        direct: delegations.filter(d => d.competenceLevel === 'Direct').length,
        coach: delegations.filter(d => d.competenceLevel === 'Coach').length,
        support: delegations.filter(d => d.competenceLevel === 'Support').length,
        delegate: delegations.filter(d => d.competenceLevel === 'Delegate').length,
        total: delegations.length
    };

    const aboveDirect = stats.coach + stats.support + stats.delegate;
    const progress = stats.total > 0 ? Math.round((aboveDirect / stats.total) * 100) : 0;

    // Custom styled badges/icons for the legend/stats
    const LevelIcon = ({ level, size = "default" }: { level: string, size?: "default" | "large" }) => {
        let icon = "🎯";
        let colorClass = "text-red-500 bg-red-50 p-1 rounded-full";
        if (level === "Coach") { icon = "🧭"; colorClass = "text-orange-500 bg-orange-50 p-1 rounded-full"; }
        if (level === "Support") { icon = "🤝"; colorClass = "text-yellow-500 bg-yellow-50 p-1 rounded-full"; }
        if (level === "Delegate") { icon = "🏁"; colorClass = "text-green-500 bg-green-50 p-1 rounded-full"; }

        // Simpler icon style for dropdown/table if needed, but keeping consistent for now
        // Overriding for "Overall Progress" header look
        if (size === "large") {
            return <span className={`text-2xl ${colorClass.split(" ")[0]}`}>{icon}</span>;
        }

        return <span className={colorClass.split(" ")[0]}>{icon}</span>;
    };

    if (!user) {
        return <div>User not found</div>;
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/development')}>
                        <ArrowLeft size={20} />
                    </Button>
                    <Avatar className="h-12 w-12 bg-slate-200">
                        <AvatarFallback>{user.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div>
                        <h1 className="text-2xl font-bold">{user.name}</h1>
                        <div className="flex items-center gap-2">
                            <span className="text-muted-foreground text-sm">Team Member</span>
                            {user.jobRole && <Badge variant="secondary" className="text-xs">{user.jobRole}</Badge>}
                        </div>
                    </div>
                </div>
                <Button onClick={handleAdd}>
                    <Plus className="mr-2 h-4 w-4" /> Add Task
                </Button>
            </div>

            {/* Overall Progress Banner */}
            <Card>
                <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="space-y-1">
                            <p className="text-sm text-muted-foreground font-medium">Overall Progress</p>
                            <div className="flex items-baseline gap-1">
                                <span className="text-5xl font-bold text-gray-900 dark:text-gray-100">{progress}%</span>
                            </div>
                            <p className="text-sm text-muted-foreground">of tasks at Coach level or above</p>
                        </div>

                        <div className="flex gap-8">
                            <div className="flex flex-col items-center gap-1">
                                <span className="text-2xl">🎯</span>
                                <span className="font-bold text-lg">{stats.direct}</span>
                                <span className="text-xs text-muted-foreground">{stats.total ? Math.round(stats.direct / stats.total * 100) : 0}%</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                                <span className="text-2xl">🧭</span>
                                <span className="font-bold text-lg">{stats.coach}</span>
                                <span className="text-xs text-muted-foreground">{stats.total ? Math.round(stats.coach / stats.total * 100) : 0}%</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                                <span className="text-2xl">🤝</span>
                                <span className="font-bold text-lg">{stats.support}</span>
                                <span className="text-xs text-muted-foreground">{stats.total ? Math.round(stats.support / stats.total * 100) : 0}%</span>
                            </div>
                            <div className="flex flex-col items-center gap-1">
                                <span className="text-2xl">🏁</span>
                                <span className="font-bold text-lg">{stats.delegate}</span>
                                <span className="text-xs text-muted-foreground">{stats.total ? Math.round(stats.delegate / stats.total * 100) : 0}%</span>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Search and Filters */}
            <div className="flex gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search tasks..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 bg-white"
                    />
                </div>
                <Select value={levelFilter} onValueChange={setLevelFilter}>
                    <SelectTrigger className="w-[180px] bg-white">
                        <div className="flex items-center gap-2">
                            <Filter className="h-4 w-4 text-muted-foreground" />
                            <SelectValue placeholder="All Levels" />
                        </div>
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Levels</SelectItem>
                        <SelectItem value="Direct">Direct</SelectItem>
                        <SelectItem value="Coach">Coach</SelectItem>
                        <SelectItem value="Support">Support</SelectItem>
                        <SelectItem value="Delegate">Delegate</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column: Data Table */}
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Delegation of Authority Table for {user.name}</CardTitle>
                            <p className="text-sm text-muted-foreground">Track competence progression across assigned tasks</p>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[40%]">Task / Activity / Decision</TableHead>
                                        <TableHead>Current Level</TableHead>
                                        <TableHead>Category</TableHead>
                                        <TableHead>Target Date</TableHead>
                                        <TableHead className="text-right">Notes</TableHead>
                                        <TableHead className="text-right">Action</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={6} className="text-center py-8">Loading...</TableCell>
                                        </TableRow>
                                    ) : filteredDelegations.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No delegation tasks found.</TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredDelegations.map((item) => (
                                            <TableRow
                                                key={item.id}
                                                className="hover:bg-slate-50 relative group"
                                            >
                                                <TableCell className="font-medium cursor-pointer" onClick={() => handleEdit(item)}>
                                                    <div>{item.task}</div>
                                                    <div className="text-xs text-muted-foreground truncate max-w-[200px]">{item.description}</div>
                                                </TableCell>
                                                <TableCell>
                                                    {/* Editable Dropdown for Level */}
                                                    <div onClick={(e) => e.stopPropagation()} className="w-[140px]">
                                                        <Select
                                                            value={item.competenceLevel}
                                                            onValueChange={(val) => handleLevelChange(item.id, val)}
                                                        >
                                                            <SelectTrigger className="h-8 border-none bg-slate-100 hover:bg-slate-200">
                                                                <div className="flex items-center gap-2">
                                                                    <LevelIcon level={item.competenceLevel} />
                                                                    <span className="text-sm font-medium">{item.competenceLevel}</span>
                                                                </div>
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="Direct">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-red-500">🎯</span> Direct
                                                                    </div>
                                                                </SelectItem>
                                                                <SelectItem value="Coach">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-orange-500">🧭</span> Coach
                                                                    </div>
                                                                </SelectItem>
                                                                <SelectItem value="Support">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-yellow-500">🤝</span> Support
                                                                    </div>
                                                                </SelectItem>
                                                                <SelectItem value="Delegate">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-green-500">🏁</span> Delegate
                                                                    </div>
                                                                </SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="cursor-pointer" onClick={() => handleEdit(item)}>
                                                    {item.category && <Badge variant="outline" className="bg-slate-100 dark:bg-slate-800">{item.category}</Badge>}
                                                </TableCell>
                                                <TableCell className="cursor-pointer" onClick={() => handleEdit(item)}>
                                                    {item.targetDate && (
                                                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                                            <Calendar size={14} />
                                                            {new Date(item.targetDate).toLocaleDateString()}
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right cursor-pointer">
                                                    {/* Notes Icon with Tooltip */}
                                                    {item.notes && (
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8 ml-auto text-muted-foreground hover:text-primary"
                                                                        onClick={(e) => handleViewNotes(item.notes, e)}
                                                                    >
                                                                        <MessageSquare size={16} />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <p>View Notes</p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(item)}>
                                                                        <Edit size={14} />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <p>Edit Task</p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>

                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={(e) => handleDeleteClick(item.id, e)}>
                                                                        <Trash2 size={14} />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <p>Delete Task</p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </div>

                {/* Right Column: Stats & Legend */}
                <div className="space-y-6">
                    {/* Progress Overview */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-base">
                                <span className="text-primary">↗</span> Progress Overview
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="text-center">
                                <div className="text-4xl font-bold">{progress}%</div>
                                <div className="text-sm text-muted-foreground">at Coach level or above</div>
                            </div>

                            <div className="space-y-4">
                                <div className="flex justify-between items-center text-sm">
                                    <div className="flex items-center gap-2">
                                        <LevelIcon level="Direct" />
                                        <span>Direct</span>
                                    </div>
                                    <div className="font-medium">{stats.direct} ({stats.total ? Math.round(stats.direct / stats.total * 100) : 0}%)</div>
                                </div>
                                <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-red-500" style={{ width: `${stats.total ? (stats.direct / stats.total * 100) : 0}%` }}></div>
                                </div>

                                <div className="flex justify-between items-center text-sm">
                                    <div className="flex items-center gap-2">
                                        <LevelIcon level="Coach" />
                                        <span>Coach</span>
                                    </div>
                                    <div className="font-medium">{stats.coach} ({stats.total ? Math.round(stats.coach / stats.total * 100) : 0}%)</div>
                                </div>
                                <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-orange-500" style={{ width: `${stats.total ? (stats.coach / stats.total * 100) : 0}%` }}></div>
                                </div>

                                <div className="flex justify-between items-center text-sm">
                                    <div className="flex items-center gap-2">
                                        <LevelIcon level="Support" />
                                        <span>Support</span>
                                    </div>
                                    <div className="font-medium">{stats.support} ({stats.total ? Math.round(stats.support / stats.total * 100) : 0}%)</div>
                                </div>
                                <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-yellow-500" style={{ width: `${stats.total ? (stats.support / stats.total * 100) : 0}%` }}></div>
                                </div>

                                <div className="flex justify-between items-center text-sm">
                                    <div className="flex items-center gap-2">
                                        <LevelIcon level="Delegate" />
                                        <span>Delegate</span>
                                    </div>
                                    <div className="font-medium">{stats.delegate} ({stats.total ? Math.round(stats.delegate / stats.total * 100) : 0}%)</div>
                                </div>
                                <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-green-500" style={{ width: `${stats.total ? (stats.delegate / stats.total * 100) : 0}%` }}></div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Competence Levels Legend */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Competence Levels</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex gap-3">
                                <div className="mt-1"><LevelIcon level="Direct" /></div>
                                <div>
                                    <div className="font-medium text-sm">Direct</div>
                                    <div className="text-xs text-muted-foreground">Manager does it, employee observes</div>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <div className="mt-1"><LevelIcon level="Coach" /></div>
                                <div>
                                    <div className="font-medium text-sm">Coach</div>
                                    <div className="text-xs text-muted-foreground">Manager guides employee doing it</div>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <div className="mt-1"><LevelIcon level="Support" /></div>
                                <div>
                                    <div className="font-medium text-sm">Support</div>
                                    <div className="text-xs text-muted-foreground">Employee leads, manager assists</div>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <div className="mt-1"><LevelIcon level="Delegate" /></div>
                                <div>
                                    <div className="font-medium text-sm">Delegate</div>
                                    <div className="text-xs text-muted-foreground">Employee independently owns it</div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-slate-50 border-slate-200 shadow-sm">
                        <CardContent className="p-4 flex items-center gap-4">
                            <div className="p-2 bg-white rounded-lg shadow-sm">
                                <span className="text-xl">📊</span>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-muted-foreground">Total Tasks</p>
                                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            <AddDelegationModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={fetchDelegations}
                employeeId={id || ""}
                delegation={selectedDelegation}
            />

            <Dialog open={isViewNotesOpen} onOpenChange={setIsViewNotesOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Task Notes</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-slate-600 whitespace-pre-wrap">{notesContent}</p>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setIsViewNotesOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Task</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this delegation task? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
